import { signIn, useSession } from "next-auth/react"
import { useRouter } from "next/router"
import * as React from "react"
import { LoginFormPure } from "../LoginFormPure/LoginFormPure"
import { checkForExtensionInstalled } from "@web/hooks/useCredentials"

export const LOGIN_NEXT_ROUTE = "LOGIN_NEXT_ROUTE"
export const IS_AUTOLOGIN = "IS_AUTOLOGIN"
export const INVITE_CODE = "INVITE_CODE"

export function updateNextRoute(nextRoute: string | null) {
  if (nextRoute) {
    window.localStorage.setItem(LOGIN_NEXT_ROUTE, nextRoute)
  } else {
    window.localStorage.removeItem(LOGIN_NEXT_ROUTE)
  }
}

export function getNextRoute() {
  return window.localStorage.getItem(LOGIN_NEXT_ROUTE)
}

export interface LoginFormProps {
  title?: string
  subTitle?: string | React.ReactNode
  autoLogin?: boolean
  inviteCode?: string | null
  forceLoading?: boolean
}

export const LoginForm: React.FC<LoginFormProps> = ({ title, subTitle, autoLogin, inviteCode, forceLoading }) => {
  const router = useRouter()
  const { data: session, status: authStatus } = useSession()
  const [isLoading, setIsLoading] = React.useState(false)
  const isAutoLoginActive = React.useRef(false)
  const isLoggedIn = !!session?.user

  const handleLogin = React.useCallback(() => {
    const next = router.query.callbackUrl
    const nextRoute = Array.isArray(next) ? next[0] : next

    if (nextRoute) window.localStorage.setItem(LOGIN_NEXT_ROUTE, nextRoute)
    else window.localStorage.removeItem(LOGIN_NEXT_ROUTE)

    setIsLoading(true)
  }, [router])

  const handleLoginEmail = React.useCallback(async () => {
    handleLogin()
    await signIn("cognito-hosted-ui")
  }, [handleLogin])

  const handleLoginLinkedIn = React.useCallback(async () => {
    handleLogin()
    await signIn("cognito").catch(console.error)
  }, [handleLogin])

  // log in extension automatically if we're logged in on the web
  React.useEffect(() => {
    if (authStatus === "loading") return
    if (authStatus === "authenticated") {
      checkForExtensionInstalled()
    }
  }, [authStatus])

  const handleAutoLogin = React.useCallback(async () => {
    // kick auth off automatically if user clicked button already
    console.debug({ authStatus, autoLogin, isLoggedIn, isAutoLoginActive: isAutoLoginActive.current })

    if (authStatus === "loading" || authStatus === "authenticated") {
      // we're already logged in, no need to auto-login
      return
    }

    if (autoLogin && !isLoggedIn && !isAutoLoginActive.current) {
      isAutoLoginActive.current = true
      window.setTimeout(async () => {
        window.localStorage.setItem(IS_AUTOLOGIN, "true")
        handleLogin()
        await signIn("cognito")
      }, 700)
    }
  }, [autoLogin, handleLogin, isLoggedIn, authStatus])

  React.useEffect(() => {
    if (inviteCode) window.localStorage.setItem(INVITE_CODE, inviteCode)

    handleAutoLogin().catch(console.error)
  }, [handleAutoLogin, inviteCode])

  return (
    <LoginFormPure
      title={title}
      subTitle={subTitle}
      onLinkedInLoginClick={handleLoginLinkedIn}
      onDevLoginClick={handleLoginEmail}
      isLoading={forceLoading ?? isLoading}
      withPaperLogo
      classes={{ adornmentLogo: "tmb-hidden", paperLogo: "tmb-mx-auto -tmb-mt-5 tmb-mb-2" }}
    />
  )
}
